/**
 * Where the story happened.
 *
 * One entry per chapter of the story trail, so the map is not a separate thing
 * to learn — it is the same twenty-eight chapters, seen from above.
 *
 * ── On the coordinates ───────────────────────────────────────────────────────
 *
 * Some of these are known to the street: Jerusalem, Rome, Babylon's mound.
 * Others are a scholar's best guess, and one or two are frankly a region rather
 * than a spot. Rather than quietly place them all with the same false
 * confidence, every entry carries a `certainty`, and the map draws the vague
 * ones as a soft area rather than a pin. A map that pretends to know where Eden
 * was is lying about the most interesting thing on it.
 *
 *   located   the site is known and excavated
 *   traditional  a long-held identification, not proven
 *   region    the text names a territory, not a place
 *   unknown   nobody knows; the position is symbolic
 *
 * Coordinates are [longitude, latitude], the order GeoJSON uses.
 */

/**
 * @typedef {object} Place
 * @property {string} chapterId  the chapter this belongs to
 * @property {string} name
 * @property {[number, number]} at  [lon, lat]
 * @property {'located'|'traditional'|'region'|'unknown'} certainty
 * @property {string} note   one line, shown when you tap the pin
 * @property {boolean} [journey]  draw a line from the previous chapter
 */

/** @type {Place[]} */
export const PLACES = [
  // ── In the beginning ──────────────────────────────────────────────────────
  {
    chapterId: 'ch-creation',
    name: 'The Garden',
    at: [47.4, 31.0],
    certainty: 'unknown',
    note: 'Placed where four rivers are said to meet, in southern Mesopotamia. Nobody knows.',
  },
  {
    chapterId: 'ch-fall',
    name: 'East of the Garden',
    at: [47.9, 31.4],
    certainty: 'unknown',
    note: 'The text says only that they went out, and east.',
  },
  {
    chapterId: 'ch-flood',
    name: 'Ararat',
    at: [44.3, 39.7],
    certainty: 'region',
    note: 'The mountains of Ararat — a range in eastern Anatolia, not one peak.',
  },
  {
    chapterId: 'ch-babel',
    name: 'Shinar',
    at: [44.4, 32.5],
    certainty: 'region',
    note: 'The plain of Shinar, southern Mesopotamia, where the great ziggurats stood.',
  },

  // ── A family chosen ───────────────────────────────────────────────────────
  {
    chapterId: 'ch-abram',
    name: 'Ur to Haran to Canaan',
    at: [39.0, 36.9],
    certainty: 'located',
    note: 'Haran, on the road between Mesopotamia and Canaan. Abram set out from Ur, far to the south-east.',
    journey: true,
  },
  {
    chapterId: 'ch-isaac',
    name: 'Moriah',
    at: [35.2, 31.8],
    certainty: 'traditional',
    note: 'Identified since antiquity with the ridge Jerusalem was later built on.',
    journey: true,
  },
  {
    chapterId: 'ch-jacob',
    name: 'The Jabbok',
    at: [35.6, 32.1],
    certainty: 'located',
    note: 'A river running west into the Jordan. Jacob crossed it alone, and did not cross back the same man.',
    journey: true,
  },
  {
    chapterId: 'ch-joseph',
    name: 'Egypt',
    at: [31.2, 30.6],
    certainty: 'region',
    note: 'Sold south into the Nile delta — the richest kingdom in the world at the time.',
    journey: true,
  },

  // ── Out of Egypt ──────────────────────────────────────────────────────────
  {
    chapterId: 'ch-bush',
    name: 'Horeb',
    at: [33.97, 28.54],
    certainty: 'traditional',
    note: 'The traditional Sinai, in the south of the peninsula. Other sites have been argued for.',
    journey: true,
  },
  {
    chapterId: 'ch-sea',
    name: 'The Sea of Reeds',
    at: [32.6, 30.0],
    certainty: 'region',
    note: 'Somewhere along the marshes and lakes between the delta and the Red Sea.',
    journey: true,
  },
  {
    chapterId: 'ch-sinai',
    name: 'Sinai',
    at: [33.97, 28.54],
    certainty: 'traditional',
    note: 'The same mountain as the bush. They came back to where it started.',
    journey: true,
  },
  {
    chapterId: 'ch-wilderness',
    name: 'Kadesh-barnea',
    at: [34.5, 30.7],
    certainty: 'traditional',
    note: 'The oasis they spent most of the forty years near — a fortnight from the land they were going to.',
    journey: true,
  },

  // ── Land and kings ────────────────────────────────────────────────────────
  {
    chapterId: 'ch-joshua',
    name: 'Jericho',
    at: [35.44, 31.87],
    certainty: 'located',
    note: 'One of the oldest continuously inhabited places on earth, and the first city across the river.',
    journey: true,
  },
  {
    chapterId: 'ch-judges',
    name: 'The hill country',
    at: [35.25, 32.2],
    certainty: 'region',
    note: 'No capital, no king — a scatter of tribes through the highlands.',
  },
  {
    chapterId: 'ch-david',
    name: 'Jerusalem',
    at: [35.23, 31.78],
    certainty: 'located',
    note: 'Taken by David and made the capital. Every later chapter comes back here.',
    journey: true,
  },
  {
    chapterId: 'ch-divided',
    name: 'Samaria and Jerusalem',
    at: [35.2, 32.28],
    certainty: 'located',
    note: 'One kingdom became two: Israel in the north, Judah in the south.',
  },

  // ── Exile and the prophets ────────────────────────────────────────────────
  {
    chapterId: 'ch-prophets',
    name: 'Israel and Judah',
    at: [35.3, 32.0],
    certainty: 'region',
    note: 'The prophets spoke in both kingdoms, mostly to people who did not want to hear it.',
  },
  {
    chapterId: 'ch-fall-jerusalem',
    name: 'Jerusalem falls',
    at: [35.23, 31.78],
    certainty: 'located',
    note: 'Besieged and burned by Babylon in 587 BC. The temple went with it.',
  },
  {
    chapterId: 'ch-babylon',
    name: 'Babylon',
    at: [44.42, 32.54],
    certainty: 'located',
    note: 'The mound of Babylon, south of modern Baghdad. Roughly where the story began.',
    journey: true,
  },
  {
    chapterId: 'ch-silence',
    name: 'Return to Jerusalem',
    at: [35.23, 31.78],
    certainty: 'located',
    note: 'Some went back and rebuilt. Then four hundred years with no prophet.',
    journey: true,
  },

  // ── The word made flesh ───────────────────────────────────────────────────
  {
    chapterId: 'ch-born',
    name: 'Bethlehem',
    at: [35.2, 31.7],
    certainty: 'located',
    note: 'Five miles from Jerusalem. David’s town, which is the point.',
  },
  {
    chapterId: 'ch-kingdom',
    name: 'Galilee',
    at: [35.58, 32.85],
    certainty: 'region',
    note: 'The lake and the villages round it — where nearly all of it was said and done.',
    journey: true,
  },
  {
    chapterId: 'ch-cross',
    name: 'Golgotha',
    at: [35.23, 31.78],
    certainty: 'traditional',
    note: 'Outside the city wall as it then ran, which is now inside the old city.',
    journey: true,
  },
  {
    chapterId: 'ch-risen',
    name: 'The garden tomb',
    at: [35.23, 31.78],
    certainty: 'traditional',
    note: 'Two sites are venerated. The text says only that it was near, and in a garden.',
  },

  // ── To the ends of the earth ──────────────────────────────────────────────
  {
    chapterId: 'ch-pentecost',
    name: 'Jerusalem',
    at: [35.23, 31.78],
    certainty: 'located',
    note: 'It starts in the city where it had just ended.',
  },
  {
    chapterId: 'ch-paul',
    name: 'Damascus road',
    at: [36.3, 33.5],
    certainty: 'region',
    note: 'A week’s walk north. He set out to arrest people and arrived unable to see.',
    journey: true,
  },
  {
    chapterId: 'ch-letters',
    name: 'Corinth, Ephesus, Philippi',
    at: [26.0, 38.5],
    certainty: 'located',
    note: 'The letters are post, written to particular congregations with particular arguments.',
    journey: true,
  },
  {
    chapterId: 'ch-new',
    name: 'Rome, and outward',
    at: [12.5, 41.9],
    certainty: 'located',
    note: 'It ends at the centre of the empire, and does not stop there.',
    journey: true,
  },
];

export const PLACES_BY_CHAPTER = Object.fromEntries(PLACES.map((p) => [p.chapterId, p]));

/** How the map draws each kind of certainty. */
export const CERTAINTY = {
  located: { label: 'Known site', pin: 'solid' },
  traditional: { label: 'Traditional site', pin: 'ring' },
  region: { label: 'A region, not a place', pin: 'area' },
  unknown: { label: 'Location unknown', pin: 'ghost' },
};

/**
 * Names on the map.
 *
 * The first version of this map was land and sea in the same colour with dots
 * on it, and no words anywhere. That is not a map, it is a shape — you cannot
 * tell which sea you are looking at, or that the dots are in the Middle East at
 * all. These are what make it legible before anyone taps anything.
 *
 * Placed by hand at the centre of each body or region. `size` picks the
 * typographic weight: seas are the quietest, regions plainer, and the two
 * empires that keep taking Israel away get named because their distance from
 * Jerusalem is half of what the map is saying.
 */
export const MAP_LABELS = [
  { text: 'MEDITERRANEAN SEA', at: [19.5, 34.6], size: 'sea', rotate: -6 },
  { text: 'RED SEA', at: [35.6, 24.4], size: 'sea', rotate: -52 },
  { text: 'THE GREAT SEA', at: [32.4, 33.4], size: 'sea-small', rotate: -8 },
  { text: 'BLACK SEA', at: [34.5, 43.2], size: 'sea-small' },
  { text: 'PERSIAN GULF', at: [49.4, 27.6], size: 'sea-small', rotate: -40 },

  { text: 'EGYPT', at: [30.4, 27.4], size: 'region' },
  { text: 'CANAAN', at: [35.1, 32.6], size: 'region' },
  { text: 'MESOPOTAMIA', at: [43.4, 34.6], size: 'region' },
  { text: 'ARABIA', at: [43.5, 24.6], size: 'region' },
  { text: 'ASIA MINOR', at: [32.5, 39.2], size: 'region' },
  { text: 'GREECE', at: [22.4, 39.4], size: 'region' },
  { text: 'ITALY', at: [13.4, 42.4], size: 'region' },
  { text: 'SINAI', at: [33.7, 29.6], size: 'region-small' },
];
